/**
 * Playbook constants — ported verbatim from the plugin so plays and formations
 * imported in Phase 8 render exactly as they did.
 */
import type { PlayPlayer, PlayType } from '../types';

export const PB_COND_W = 300, PB_COND_H = 300;
export const PB_SPR_W = 300, PB_SPR_H = 150;
export const PB_LOS_PCT = 0.52, PB_Y5_PCT = 0.28, PB_Y10_PCT = 0.1, PB_NAME_PCT = 0.13;

// ── Scratch pad field geometry ─────────────────────────────────────────────
// Unlike the play cards (compressed to fit a printed 4.5"×2.25" card), the
// scratch pad is drawn to true field proportions: yards-per-pixel is identical
// on both axes. The view is 44 yards wide rather than the full 53⅓.
export const PB_SCR_FIELD_W_YD = 44;
export const PB_SCR_YD_UP = 15, PB_SCR_YD_DOWN = 10;
export const PB_SCR_YD_TOTAL = PB_SCR_YD_UP + PB_SCR_YD_DOWN;
export const PB_SCR_H = 350;
export const PB_SCR_W = Math.round((PB_SCR_H * PB_SCR_FIELD_W_YD) / PB_SCR_YD_TOTAL); // 616
export const PB_SCR_LOS_PCT = PB_SCR_YD_UP / PB_SCR_YD_TOTAL; // 0.60
/** HS hashes are 53'4" (17.78yd) in from each sideline, i.e. 8.885yd either side of centre. */
export const PB_SCR_HASH_OFF = 8.885 / PB_SCR_FIELD_W_YD;
export const PB_SCR_ICON_REF = 0.39;

export interface PositionStyle {
  fill: string;
  stroke: string;
  text: string;
  label: string;
  isLine: boolean;
}

export const PB_COLORS: Record<string, PositionStyle> = {
  // Was 'Center' — too long for a line card, and every built-in formation already says 'C'.
  C: { fill: '#c9c6ef', stroke: '#7070b0', text: '#000', label: 'C', isLine: true },
  LT: { fill: '#c9c6ef', stroke: '#7070b0', text: '#000', label: 'LT', isLine: true },
  LG: { fill: '#c9c6ef', stroke: '#7070b0', text: '#000', label: 'LG', isLine: true },
  RG: { fill: '#c9c6ef', stroke: '#7070b0', text: '#000', label: 'RG', isLine: true },
  RT: { fill: '#c9c6ef', stroke: '#7070b0', text: '#000', label: 'RT', isLine: true },
  // Was 'Pull Guard'. 'P' until a rule replaces it — the rule is what he reads on the card.
  PULL: { fill: '#8e2ee0', stroke: '#5010a0', text: '#fff', label: 'P', isLine: true },
  // Backs are numbered rather than lettered: Q=1, H=2, F=3, Z=4. The ends (X, Y) stay lettered.
  QB: { fill: '#ee44aa', stroke: '#bb1177', text: '#fff', label: '1', isLine: false },
  X: { fill: '#003399', stroke: '#001166', text: '#fff', label: 'X', isLine: false },
  Y: { fill: '#22aa44', stroke: '#116622', text: '#fff', label: 'Y', isLine: false },
  Z: { fill: '#ff7d00', stroke: '#bb4400', text: '#fff', label: '4', isLine: false },
  H: { fill: '#cc2222', stroke: '#881111', text: '#fff', label: '2', isLine: false },
  F: { fill: '#55ccee', stroke: '#2299bb', text: '#fff', label: '3', isLine: false },
  DEF: { fill: '#e03030', stroke: '#a01010', text: '#fff', label: 'DEF', isLine: false },
  BALL: { fill: '#cc7700', stroke: '#000000', text: '#000', label: 'Ball', isLine: false },
  // Field marker, not a player: a bare orange triangle with no label.
  CONE: { fill: '#ff7a18', stroke: '#a34200', text: '#000', label: 'Cone', isLine: false },
  // ── Drill props (added for drill diagrams) ────────────────────────────────
  // Every entry below is a NEW type code. Nothing above this line changed, so
  // plays and cards built before drill diagrams existed render exactly as they
  // did — the golden SVG test pins that.
  BAG: { fill: '#2f6f4f', stroke: '#1b422f', text: '#fff', label: 'Bag', isLine: false },
  SLED: { fill: '#555f6b', stroke: '#2c333b', text: '#fff', label: 'Sled', isLine: false },
  HOOP: { fill: '#ffd24d', stroke: '#b98a00', text: '#000', label: 'Hoop', isLine: false },
  /** A line of players waiting a rep. `label` holds the count ("5"); default 3. */
  QUEUE: { fill: '#6b7fd7', stroke: '#3a4a97', text: '#fff', label: 'Line', isLine: false },
  COACH: { fill: '#1a1a16', stroke: '#000000', text: '#fff', label: 'Coach', isLine: false },
};

/** Type codes the drill-diagram palette offers in addition to the positions. */
export const PB_PROP_TYPES = ['CONE', 'BAG', 'SLED', 'HOOP', 'QUEUE', 'COACH'] as const;

/**
 * The playbook's own palette: everything except the drill props. CONE and BALL
 * stay in it because they were in it before drill diagrams existed and plays
 * already use them.
 */
export const PB_POSITION_TYPES = ['C', 'LT', 'LG', 'RG', 'RT', 'PULL', 'QB', 'X', 'Y', 'Z', 'H', 'F', 'DEF', 'BALL', 'CONE'] as const;

const PB_PROP_SET = new Set<string>(PB_PROP_TYPES);
/** Equipment and markers: drawn as objects, never faded, never given a rule. */
export function pbIsProp(t: string): boolean {
  return PB_PROP_SET.has(t);
}

const PB_STATIC_SET = new Set(['CONE', 'BAG', 'SLED', 'HOOP', 'BALL']);
/** Props that stay put: they are never adopted as the actor of an animated line. */
export function pbIsStaticProp(t: string): boolean {
  return PB_STATIC_SET.has(t);
}

export const PB_RULES = ['', 'G', 'H', 'L', 'K', 'R', 'B', 'C', 'Cu'];
export const PB_RULE_LABELS: Record<string, string> = {
  '': '—',
  G: 'G Gap',
  H: 'H Hinge',
  L: 'L Lead',
  K: 'K Kickout',
  R: 'R Reach',
  B: 'B Backer',
  C: 'C Combo',
  Cu: 'Cu Cut',
};

export const PB_DEFAULT_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(PB_COLORS).map(([k, v]) => [k, v.label]),
);

export const PB_BUILTIN_FORMATIONS: { name: string; players: PlayPlayer[] }[] = [
  {
    name: 'Double Wing',
    players: [
      { type: 'LT', label: 'LT', x: 0.2872, y: 0.5177 }, { type: 'LG', label: 'LG', x: 0.3891, y: 0.5208 },
      { type: 'C', label: 'C', x: 0.4848, y: 0.4899 }, { type: 'RG', label: 'RG', x: 0.5805, y: 0.5177 },
      { type: 'RT', label: 'RT', x: 0.6823, y: 0.5239 }, { type: 'QB', label: '1', x: 0.4879, y: 0.6041 },
      { type: 'F', label: '3', x: 0.4909, y: 0.743 }, { type: 'H', label: '2', x: 0.0897, y: 0.6195 },
      { type: 'Y', label: 'Y', x: 0.1761, y: 0.5208 }, { type: 'Z', label: '4', x: 0.8891, y: 0.601 },
      { type: 'X', label: 'X', x: 0.7965, y: 0.5115 },
    ],
  },
  {
    name: 'Spread Left',
    players: [
      { type: 'LT', label: 'LT', x: 0.3335, y: 0.5115 }, { type: 'LG', label: 'LG', x: 0.4354, y: 0.5115 },
      { type: 'C', label: 'C', x: 0.528, y: 0.4714 }, { type: 'RG', label: 'RG', x: 0.6144, y: 0.5208 },
      { type: 'RT', label: 'RT', x: 0.7132, y: 0.5208 }, { type: 'Y', label: 'Y', x: 0.0557, y: 0.5045 },
      { type: 'X', label: 'X', x: 0.8366, y: 0.527 }, { type: 'QB', label: '1', x: 0.528, y: 0.5979 },
      { type: 'F', label: '3', x: 0.5311, y: 0.7307 }, { type: 'H', label: '2', x: 0.1916, y: 0.6134 },
      { type: 'Z', label: '4', x: 0.92, y: 0.6257 },
    ],
  },
  {
    name: 'Spread Right',
    players: [
      { type: 'LT', label: 'LT', x: 0.2533, y: 0.5208 }, { type: 'LG', label: 'LG', x: 0.3551, y: 0.5177 },
      { type: 'C', label: 'C', x: 0.4385, y: 0.4652 }, { type: 'RG', label: 'RG', x: 0.528, y: 0.5177 },
      { type: 'RT', label: 'RT', x: 0.6298, y: 0.5177 }, { type: 'Y', label: 'Y', x: 0.1391, y: 0.5177 },
      { type: 'X', label: 'X', x: 0.9354, y: 0.527 }, { type: 'QB', label: '1', x: 0.4385, y: 0.5918 },
      { type: 'F', label: '3', x: 0.4261, y: 0.7245 }, { type: 'H', label: '2', x: 0.0835, y: 0.6257 },
      { type: 'Z', label: '4', x: 0.7842, y: 0.635 },
    ],
  },
];

export const PB_MAX_UNDO = 30;
export const PB_LINE_ICON_SCALE = 1.5;

export function pbGetPlayDims(ptype: PlayType | string | undefined): { w: number; h: number } {
  if (ptype === 'scratch' || ptype === 'drill') return { w: PB_SCR_W, h: PB_SCR_H };
  return ptype === 'spread' ? { w: PB_SPR_W, h: PB_SPR_H } : { w: PB_COND_W, h: PB_COND_H };
}

/** Drill diagrams share the scratch pad's true-field proportions. */
export const PB_DRILL_W = PB_SCR_W, PB_DRILL_H = PB_SCR_H;

export function pbIsLineman(t: string): boolean {
  return PB_COLORS[t]?.isLine || false;
}

/** The offense's backs and receivers — the players a line card leaves off. */
export const PB_SKILL_TYPES = new Set(['QB', 'X', 'Y', 'Z', 'H', 'F']);

/**
 * What a player's marker says: his rule if he has one, else his label.
 * Centers and pullers placed before the short defaults were saved as 'Center'
 * and 'Pull Guard'; show those as 'C' and 'P' too, without rewriting the play.
 */
export function pbMarkerLabel(p: { type: string; label?: string; rule?: string }): string {
  if (p.rule) return p.rule;
  if (p.type === 'C' && p.label === 'Center') return 'C';
  if (p.type === 'PULL' && p.label === 'Pull Guard') return 'P';
  return p.label || p.type;
}

/**
 * Base player-icon radius. refW is the width condensed plays use at this same
 * scale, so spread icons render at the identical absolute size as condensed.
 * Line cards bump prominent icons by PB_LINE_ICON_SCALE (print legibility).
 */
export function pbBaseRadius(W: number, _H: number, refW?: number, mode?: string): number {
  const base = (refW !== undefined ? refW : W) * 0.05;
  return mode === 'line' ? base * PB_LINE_ICON_SCALE : base;
}
